import React, { useCallback, useEffect, useState } from "react";
import { useAppDispatch } from "../../../../state/store";
import { selectCareRecipientAction } from "../../../../state/usecase/care_recipient/actions";
import { CareRecipient } from "../../../../domain/care_recipient/CareRecipient";
import { DropDownIcon } from "../icons/DropDownIcon";
import { useSelectedCareRecipient } from "../../../hooks/care_recipient/useSelectedCareRecipient";
import { useBoolean } from "../../../hooks/common/useBoolean";
import OutsideClickHandler from "react-outside-click-handler";
import { AdminOnlyComponents } from "../../admin/components/AdminOnlyComponents";
import classNames from "classnames";

export default function CareRecipientSelectButton(props: Props) {
    const { careRecipients } = props;

    const selectedCareRecipient = useSelectedCareRecipient();

    const [isOpenDropDown, , closeDropDown, toggle] = useBoolean();

    const [careRecipientName, setCareRecipientName] = useState("未選択");

    const dispatch = useAppDispatch();

    const handleOnChange = useCallback((careRecipient: CareRecipient) => () => {
        // すでに選択済の利用者をクリックしても何もしない
        if (careRecipient.customerCode?.value === selectedCareRecipient?.customerCode?.value) return;

        setCareRecipientName(careRecipient.name.value);
        dispatch(selectCareRecipientAction(
            {
                id: careRecipient.id,
                name: careRecipient.name,
                houseId: careRecipient.houseId,
                branchCode: careRecipient.branchCode,
                customerCode: careRecipient.customerCode,
            }
        ));

        closeDropDown();
    }, [selectedCareRecipient, dispatch, closeDropDown]);

    useEffect(() => {
        if (!careRecipients) return;

        if (!selectedCareRecipient) {
            setCareRecipientName("未選択");
            return;
        }

        setCareRecipientName(selectedCareRecipient.name.value);
    }, [careRecipients, selectedCareRecipient]);

    const renderDropDown = useCallback(() => {
        if (!isOpenDropDown) return null;

        return (
            <div className={"care_recipient_name__drop_down"}>
                {careRecipients?.map(careRecipient => {
                    const className = classNames(
                        "name",
                        {
                            // 1行に4人ずつ描写するが、1行しかない（4人以下）の場合に下線を引くと見た目の違和感があるため、2行以上ある時のみ下線を引く
                            "border": careRecipients?.length > 4,
                            "selected": careRecipient.id.value === selectedCareRecipient?.id.value
                        }
                    );

                    return (
                        <div
                            key={careRecipient.id.value}
                            className={className}
                            onClick={handleOnChange(careRecipient)}
                        >
                            {careRecipient.name.value}
                            <AdminOnlyComponents>
                                <div className={"tooltip"}>
                                    {careRecipient.houseId?.value}
                                </div>
                            </AdminOnlyComponents>
                        </div>
                    );
                })}
            </div>
        );
    }, [isOpenDropDown, careRecipients, handleOnChange, selectedCareRecipient]);

    const renderButton = useCallback(() => {
        if (careRecipients?.length === 0) {
            return null;
        }

        return (
            <>
                <div className={"title"}>ご利用者名</div>
                <OutsideClickHandler onOutsideClick={closeDropDown}>
                    <div onClick={toggle} aria-disabled={isOpenDropDown}>
                        <AdminOnlyComponents>
                            <div className={"selected_house_id"}>
                                {selectedCareRecipient?.houseId?.value}
                            </div>
                        </AdminOnlyComponents>
                        <div className={"selected_name"}>
                            {careRecipientName}
                            <div className={`drop_down_icon__${isOpenDropDown ? "open" : "close"}`}>
                                <DropDownIcon/>
                            </div>
                        </div>
                    </div>
                    {renderDropDown()}
                </OutsideClickHandler>
            </>
        );
    }, [careRecipientName, careRecipients, closeDropDown, isOpenDropDown, renderDropDown, selectedCareRecipient, toggle]);

    return (
        <div className={"care_recipient_name"}>
            {renderButton()}
        </div>
    );
}

type Props = {
    careRecipients: CareRecipient[] | null
}
